import { sanitizeDescription } from '@publicss/utils';
import { graphql } from 'gatsby';
import PropType from 'prop-types';
import React from 'react';

import { Meta } from '../components/Meta';
import BlockContactSlice from '../components/slices/BlockContactSlice';
import BlogPost from '../intl-blogs/apac-blog/components/BlogPost';
import { Schemas } from '../seo/Schemas';
import { getArticleSchemaDatePublished } from '../utils/dates';

export const Head = ({ data, pageContext, location }) => {
  const { siteURL } = pageContext;
  const page = data.allPrismicPage?.edges[0]?.node.data;
  const title = data.post.seo?.title || data.post.title || page?.seo_title;
  const description =
    data.post.seo?.metaDesc ||
    sanitizeDescription(data.post.excerpt) ||
    page?.seo_description;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${pageContext.siteURL}${location.pathname}`,
    },
    headline: data?.post?.seo?.title ?? data?.post?.title,
    description: data?.post?.seo?.metaDesc,
    image: data?.post?.featuredImage?.node?.localFile?.childImageSharp?.fluid
      ?.src
      ? `${pageContext.siteURL}${data?.post?.featuredImage?.node?.localFile?.childImageSharp?.fluid?.src}`
      : pageContext.global.social_share_image?.url,
    author: {
      '@type': 'Person',
      name: data?.post?.seo?.opengraphAuthor,
    },
    publisher: {
      '@type': 'Organization',
      name: pageContext?.siteName,
      logo: {
        '@type': 'ImageObject',
        url: pageContext?.global?.text_logo?.url,
      },
    },
    datePublished: getArticleSchemaDatePublished(data?.post?.date),
  };

  return (
    <>
      <Meta
        pageContext={pageContext}
        pageTitle={title}
        title={title}
        description={description}
        url={`${siteURL}/${location.pathname}`}
      />
      <Schemas data={data} pageContext={pageContext} />
      <script type="application/ld+json">
        {JSON.stringify(articleSchema, null, 2)}
      </script>
    </>
  );
};

const BlogPostTemplate = ({
  data: { previous, post, next, allPrismicPage },
}) => {
  const doc = allPrismicPage?.edges?.[0]?.node?.data;

  const contactSlice = doc?.body?.find(
    (slice) => slice.slice_type === 'block_contact'
  );

  return (
    <>
      <article
        className="blog-post"
        itemScope
        itemType="http://schema.org/Article">
        <BlogPost post={post} previous={previous} next={next} />
      </article>
      {contactSlice && <BlockContactSlice slice={contactSlice} />}
    </>
  );
};

BlogPostTemplate.propTypes = {
  data: PropType.exact({
    previous: PropType.object,
    next: PropType.object,
    post: PropType.shape({
      title: PropType.string,
      excerpt: PropType.string,
      featuredImage: PropType.object,
      uri: PropType.string,
    }),
    allPrismicPage: PropType.object,
  }),
};

export default BlogPostTemplate;

export const pageQuery = graphql`
  query BlogPostById(
    # these variables are passed in via createPage.pageContext in gatsby-node.js
    $id: String!
    $previousPostId: String
    $nextPostId: String
    $siteTag: String!
    $siteLang: String!
  ) {
    allPrismicPage(
      filter: {
        tags: { eq: $siteTag }
        data: { slug: { regex: "/^/?blog/?$/" }, locale: { eq: $siteLang } }
      }
    ) {
      edges {
        node {
          data {
            slug
            seo_title
            seo_description
            body {
              ... on PrismicPageDataBodyBlockContact {
                slice_type
                primary {
                  background_image {
                    url
                    alt
                  }
                  button {
                    html
                    richText
                    text
                  }
                  color
                  content {
                    html
                    richText
                    text
                  }
                  title_end {
                    html
                    richText
                    text
                  }
                  title_middle {
                    html
                    richText
                    text
                  }
                  title_start {
                    html
                    richText
                    text
                  }
                }
              }
            }
          }
        }
      }
    }

    # selecting the current post by id
    post: wpPost(id: { eq: $id }) {
      id
      excerpt
      content
      title
      date(formatString: "MMMM DD, YYYY")
      uri

      featuredImage {
        node {
          altText
          localFile {
            childImageSharp {
              fluid(maxWidth: 1000, quality: 100) {
                ...GatsbyImageSharpFluid_tracedSVG
              }
            }
          }
        }
      }
    }

    # this gets us the previous post by id (if it exists)
    previous: wpPost(id: { eq: $previousPostId }) {
      uri
      title
      date(formatString: "MMMM DD, YYYY")
      featuredImage {
        node {
          altText
          localFile {
            childImageSharp {
              fluid(maxWidth: 1000, quality: 100) {
                ...GatsbyImageSharpFluid_tracedSVG
              }
            }
          }
        }
      }
    }

    # this gets us the next post by id (if it exists)
    next: wpPost(id: { eq: $nextPostId }) {
      uri
      title
      date(formatString: "MMMM DD, YYYY")
      featuredImage {
        node {
          altText
          localFile {
            childImageSharp {
              fluid(maxWidth: 1000, quality: 100) {
                ...GatsbyImageSharpFluid_tracedSVG
              }
            }
          }
        }
      }
    }
  }
`;
